import { Injectable } from '@angular/core';
import { Firestore, collection, addDoc, collectionData, updateDoc ,  Timestamp,doc, collection as subcollection, writeBatch, getDocs, getDoc, query, where, docData, deleteField } from '@angular/fire/firestore';
import { combineLatest, map, Observable, of, switchMap, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class RentalApplicationService {
  constructor(private firestore: Firestore) {}

  async submitApplication(rentalId: string, applicationData: any) {
    const rentalRef = doc(this.firestore, `rentals/${rentalId}`);
    const applicationsRef = subcollection(rentalRef, 'applications');
    return await addDoc(applicationsRef, {
      ...applicationData,
      submittedAt: new Date()
    });
  }
  // Update the status of an application
  updateApplicationStatus(
    rentalId: string,
    applicationId: string,
    status: 'approved' | 'rejected'
  ) {
    const appRef = doc(
      this.firestore,
      `rentals/${rentalId}/applications/${applicationId}`
    );
    return updateDoc(appRef, { status });
  }
  async openApplicationRound(rental: any) {
  if (rental.activeRoundId) return;

  const roundRef = await addDoc(
    collection(this.firestore, 'applicationRounds'),
    {
      rentalId: rental.id,
      status: 'open',
      openedAt: new Date()
    }
  );

  await updateDoc(doc(this.firestore, 'rentals', rental.id), {
    activeRoundId: roundRef.id
  });
}
async closeRound(roundId: string, approvedApplicationId: string) {
  const appsRef = collection(
    this.firestore,
    `applicationRounds/${roundId}/applications`
  );

  const appsSnap = await getDocs(appsRef);

  const batch = writeBatch(this.firestore);

  appsSnap.forEach(docSnap => {
    if (docSnap.id !== approvedApplicationId) {
      batch.update(docSnap.ref, { status: 'rejected' });
    }
  });

  batch.update(
    doc(this.firestore, 'applicationRounds', roundId),
    {
      status: 'closed',
      closedAt: new Date()
    }
  );

  await batch.commit();
}
async submitApplicationForRental(rentalId: string, payload: any) {
    const rentalRef = doc(this.firestore, 'rentals', rentalId);
    const rentalSnap = await getDoc(rentalRef);

    if (!rentalSnap.exists()) {
      throw new Error('Rental not found');
    }

    const rental = rentalSnap.data();

    if (!rental['activeRoundId']) {
      throw new Error('No active application round');
    }

    const roundId = rental['activeRoundId'];

    return addDoc(
      collection(
        this.firestore,
        `applicationRounds/${roundId}/applications`
      ),
      {
        ...payload,
        status: 'pending',
        roundId,
        rentalId,
        createdAt: new Date()
      }
    );
  }
 async approveApplicationInRound(
  roundId: string,
  rentalId: string,
  applicationId: string
): Promise<void> {

  const appsRef = collection(
    this.firestore,
    `applicationRounds/${roundId}/applications`
  );

  const appsSnap = await getDocs(appsRef);
  const batch = writeBatch(this.firestore);

  appsSnap.forEach(docSnap => {
    batch.update(docSnap.ref, {
      status: docSnap.id === applicationId ? 'approved' : 'rejected',
      decidedAt: Timestamp.now()
    });
  });

  const roundRef = doc(this.firestore, 'applicationRounds', roundId);
  batch.set(roundRef, {
    status: 'closed',
    closedAt: Timestamp.now()
  }, { merge: true });

  const rentalRef = doc(this.firestore, 'rentals', rentalId);
  batch.update(rentalRef, {
    activeRoundId: deleteField(),
    status: 'occupied' // optional business rule
  });

  await batch.commit();
}


getApplicationsByRoundId(roundId: string): Observable<any[]> {
  const applicationsRef = collection(
    this.firestore,
    `applicationRounds/${roundId}/applications`
  );

  return collectionData(applicationsRef, { idField: 'id' });
}


getApplicationRoundsWithApplications(): Observable<any[]> {

  const roundsRef = collection(this.firestore, 'applicationRounds');

  const q = query(
    roundsRef,
    where('status', '==', 'open')
  );

  return collectionData(q, { idField: 'id' }).pipe(
    switchMap((rounds: any[]) => {

      if (!rounds.length) return of([]);

      return combineLatest(
        rounds.map(round =>
          combineLatest([
            // Applications (subcollection)
            this.getApplicationsByRoundId(round.id),

            // Rental document
            docData(
              doc(this.firestore, 'rentals', round.rentalId),
              { idField: 'id' }
            )
          ]).pipe(
            map(([applications, rental]) => ({
              ...round,
              rental,
              applications
            }))
          )
        )
      );

    })
  );
}

 getApplicationById(
    roundId: string,
    applicationId: string
  ): Observable<any> {

    const appRef = doc(
      this.firestore,
      'applicationRounds',
      roundId,
      'applications',
      applicationId
    );

    return docData(appRef, { idField: 'id' });
  }
  getApplicationsGroupedByRental(): Observable<any[]> {
  const rentalsRef = collection(this.firestore, 'rentals');

  return collectionData(rentalsRef, { idField: 'rentalId' }).pipe(
    switchMap((rentals: any[]) => {
      if (!rentals.length) return of([]);

      const apps$ = rentals.map(rental =>
        collectionData(
          collection(this.firestore, `rentals/${rental.rentalId}/applications`),
          { idField: 'applicationId' }
        ).pipe(
          map(apps => ({
            rentalId: rental.rentalId,
            rentalAddress: rental.address,
            rentalPrice: rental.price,
            applications: apps
          }))
        )
      );

      return combineLatest(apps$);
    })
  );
}

async rejectApplicationInRound(
  roundId: string,
  applicationId: string
): Promise<void> {

  const appRef = doc(
    this.firestore,
    `applicationRounds/${roundId}/applications/${applicationId}`
  );

  await updateDoc(appRef, {
    status: 'rejected',
    decidedAt: Timestamp.now()
  });
}

  getAllApplicationsWithRental(): Observable<any[]> {
    const rentalsRef = collection(this.firestore, 'rentals');

    return collectionData(rentalsRef, { idField: 'rentalId' }).pipe(
      switchMap((rentals: any[]) => {
        if (!rentals.length) return of([]);

        const apps$ = rentals.map(rental =>
          collectionData(
            collection(this.firestore, `rentals/${rental.rentalId}/applications`),
            { idField: 'applicationId' }
          ).pipe(
            map(apps =>
              apps.map(app => ({
                ...app,
                rentalId: rental.rentalId,
                rentalAddress: rental.address,
                rentalPrice: rental.price
              }))
            )
          )
        );

        return combineLatest(apps$).pipe(
          map(results => results.flat())
        );
      })
    );
  }

}
